import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FloatingAgentCircle } from "./floating-agent-circle";
import { FloatingAgentPanel } from "./floating-agent-panel";
import { FloatingAgentPreviewBubble } from "./floating-agent-preview-bubble";
import { useFloatingAgentReplies } from "./floating-agent-replies";
import { useFloatingAgentVoiceHold } from "./floating-agent-voice-hold";
import { FloatingAgentComposerRegistry } from "./floating-agent-composer-mentions";
import {
  FloatingAgentPanelOpenTarget,
  MOBILE_WEB_WIDGET_HOST_INSET_PX,
  MOBILE_WEB_WIDGET_STACK_GAP_PX,
  maxExpandedPeekBubbleHeightPx,
  resolveFloatingAgentOpenTarget,
  WEB_WIDGET_HOST_INSET_PX,
  WEB_WIDGET_STACK_GAP_PX,
} from "./floating-agent-constants";
import { useAppStore } from "@/store";
import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import { FloatingAgentWindowAnchor, type FloatingAgentEvent } from "@/modules/electron/types";
import {
  getDefaultMemAgentHoldToTalkKey,
  getMemAgentHoldToTalkKeyDisplay,
} from "@/modules/mem-agent/hold-to-talk-key";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { MDSComboboxMention } from "@/mds/components/forms";
import { SessionStatus } from "@/mds/components/sessions/session.types";
import { MDSTooltip, MDSTooltipTrigger } from "@/mds/components/tooltip";
import { MDSTooltipPlacement } from "@/mds/components/tooltip/tooltip.types";
import { MDSBreakpoints, MDSZIndexes, ScreenSize } from "@/mds/foundations";
import { ONBOARDING_STEPS_WITH_FLOATING_AGENT } from "@/store/floating-agent/floating-agent-onboarding-steps";
import { getNextInputRequestValue } from "@/components/active-session/helpers/input-request-helpers";
import { canRenderForm } from "@/components/active-session/input-forms";
import {
  readIMessageClaimCompletionSurfaceExited,
  readIMessageClaimFlowSession,
} from "@/modules/mem-agent/imessage-claim-flow-session";

/**
 * The in-app widget host: the same circle/peek/panel widget as the standalone
 * desktop window, rendered as a fixed bottom-right overlay inside the app
 * page. Web always uses this surface; desktop uses it in Docked mode.
 */
export const FloatingAgentWebWidget: FC<{ suppressForImportFlow?: boolean }> = observer(
  ({ suppressForImportFlow = false }) => {
    const { store } = useAppStore();
    const isDesktop = clientEnvModule.isDesktop();
    const presentationLocation = store.floatingAgent.presentationLocation;
    const presentationTransitionId = store.floatingAgent.presentationTransitionId;
    const isActiveFileImportAwaitingInput = store.sessions.activeImportSessions.some(session => {
      const nextInputRequest = getNextInputRequestValue(session.eventList.allSessionEvents);

      return canRenderForm(nextInputRequest?.form);
    });
    const shouldSuppressForImportFlow =
      suppressForImportFlow || store.oneClickImport.isModalOpen || isActiveFileImportAwaitingInput;

    useEffect(() => {
      if (
        !isDesktop ||
        presentationLocation === "docked" ||
        typeof presentationTransitionId !== "number" ||
        !Number.isSafeInteger(presentationTransitionId)
      ) {
        return;
      }

      let ackSent = false;
      let secondFrameId: number | null = null;
      const firstFrameId = requestAnimationFrame(() => {
        secondFrameId = requestAnimationFrame(() => {
          ackSent = true;
          logger.info({
            message: "[FloatingAgentWebWidget] docked-release ack sent",
            info: { presentationTransitionId },
          });
          void electronModule.floatingAgent.presentationReleased({
            presentationTransitionId,
          });
        });
      });

      return () => {
        cancelAnimationFrame(firstFrameId);
        if (secondFrameId !== null) cancelAnimationFrame(secondFrameId);
        // A canceled ack strands the main process's docked-release latch and
        // keeps the widget window invisible (MEM-19150). Deps changing before
        // the two frames elapse is the suspected loss mechanism.
        if (!ackSent) {
          logger.warn({
            message: "[FloatingAgentWebWidget] docked-release ack canceled before send",
            info: { presentationTransitionId },
          });
        }
      };
    }, [isDesktop, presentationLocation, presentationTransitionId]);

    const currentOnboardingStepId = store.onboarding.currentStepId;
    const onboardingStepNeedsFloatingAgent =
      store.onboarding.isActive &&
      currentOnboardingStepId !== undefined &&
      ONBOARDING_STEPS_WITH_FLOATING_AGENT.has(currentOnboardingStepId);
    const canRenderForOnboarding =
      onboardingStepNeedsFloatingAgent &&
      store.floatingAgent.isOnboardingSurfaceAvailable &&
      !store.floatingAgent.isSuppressedForBootstrap &&
      !store.floatingAgent.isSuppressedForOnboarding;
    const canRenderAfterIMessageClaimCompletion =
      readIMessageClaimFlowSession() &&
      readIMessageClaimCompletionSurfaceExited() &&
      store.floatingAgent.isOnboardingSurfaceAvailable &&
      !store.floatingAgent.isSuppressedForBootstrap &&
      !store.floatingAgent.isSuppressedForLogout;

    // Floating desktop mode keeps its separate OS-window widget; mounting here
    // too would double-render it. Docked desktop can still render after the
    // iMessage claim completion surface exits because normal onboarding
    // suppression keeps `isDockedWidgetEnabled` false.
    if (
      isDesktop &&
      !store.floatingAgent.isDockedWidgetEnabled &&
      !(presentationLocation === "docked" && canRenderAfterIMessageClaimCompletion)
    ) {
      return null;
    }

    if (shouldSuppressForImportFlow) return null;
    if (
      !store.floatingAgent.isEnabled &&
      !canRenderForOnboarding &&
      !canRenderAfterIMessageClaimCompletion
    ) {
      return null;
    }
    if (
      store.onboarding.isActive &&
      !canRenderAfterIMessageClaimCompletion &&
      (!currentOnboardingStepId ||
        !ONBOARDING_STEPS_WITH_FLOATING_AGENT.has(currentOnboardingStepId))
    ) {
      return null;
    }

    return <FloatingAgentWebWidgetContent />;
  }
);

type WidgetMode = "circle" | "panel" | "peek";

const readInteractionLeaseId = (interactionLeaseId: number | undefined): number | null =>
  typeof interactionLeaseId === "number" && Number.isSafeInteger(interactionLeaseId)
    ? interactionLeaseId
    : null;

const readVoiceCountdown = (
  event: FloatingAgentEvent
): { countdownStartedAtMs: number; countdownDeadlineMs: number } | null => {
  const { countdownStartedAtMs, countdownDeadlineMs } = event;
  if (
    countdownStartedAtMs === undefined ||
    countdownDeadlineMs === undefined ||
    !Number.isFinite(countdownStartedAtMs) ||
    !Number.isFinite(countdownDeadlineMs) ||
    countdownDeadlineMs <= countdownStartedAtMs
  ) {
    return null;
  }

  return { countdownStartedAtMs, countdownDeadlineMs };
};

const FloatingAgentWebWidgetContent: FC = observer(() => {
  const { store, pageStore } = useAppStore();
  const floatingAgentPage = pageStore.floatingAgentPage;
  const isThinking = floatingAgentPage.session?.modelData?.status === SessionStatus.Processing;
  const [mode, setMode] = useState<WidgetMode>("circle");
  const modeRef = useRef<WidgetMode>("circle");
  modeRef.current = mode;
  const isPanelOpen = useCallback(() => modeRef.current === "panel", []);
  const interactionLeaseIdRef = useRef<number | null>(null);
  const interactionLeaseAcquireGenerationRef = useRef(0);
  const interactionLeaseAcquirePromiseRef = useRef<Promise<number | null> | null>(null);
  const lastVoiceTurnCompletionIdRef = useRef(0);
  const panelExpansionPendingRef = useRef(false);
  const [panelOpenTarget, setPanelOpenTarget] = useState<FloatingAgentPanelOpenTarget | null>(null);
  const [isOnboardingNudging, setIsOnboardingNudging] = useState(false);
  const onboardingNudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The widget shares the page with the app: the foregrounded-note chips come
  // from a live read of the visible panes, no IPC round-trip. They only apply
  // while the app itself is foregrounded (visible and focused) — a backgrounded
  // app foregrounds no note, so it shows no chips.
  const isAppForegrounded = store.tabLifecycleManager.isTabActive;
  const foregroundNotes = store.floatingAgent.foregroundNotes;
  const foregroundMentions = useMemo<MDSComboboxMention[]>(
    () =>
      isAppForegrounded
        ? foregroundNotes.map(note => ({
            kind: MemCommonMentionKind.Note,
            // External marks the chip non-removable.
            isExternal: true,
            note_id: note.noteId,
            note_primary_label: note.label,
          }))
        : [],
    [foregroundNotes, isAppForegrounded]
  );
  const foregroundMentionsRef = useRef(foregroundMentions);
  foregroundMentionsRef.current = foregroundMentions;
  const composerRegistryRef = useRef<FloatingAgentComposerRegistry>({
    main: null,
    thread: null,
  });
  const invalidatePendingInteractionLeaseAcquire = useCallback(() => {
    interactionLeaseAcquireGenerationRef.current += 1;
    interactionLeaseAcquirePromiseRef.current = null;
  }, []);
  const adoptInteractionLease = useCallback(
    (interactionLeaseId: number | undefined): number | null => {
      const transferredLeaseId = readInteractionLeaseId(interactionLeaseId);
      if (transferredLeaseId === null) return null;

      invalidatePendingInteractionLeaseAcquire();
      interactionLeaseIdRef.current = transferredLeaseId;
      return transferredLeaseId;
    },
    [invalidatePendingInteractionLeaseAcquire]
  );

  useEffect(
    () => () => {
      if (onboardingNudgeTimerRef.current) clearTimeout(onboardingNudgeTimerRef.current);
      invalidatePendingInteractionLeaseAcquire();
    },
    [invalidatePendingInteractionLeaseAcquire]
  );

  // Break the cycle: the voice-hold hook needs to open the panel, and
  // expandToPanel is defined below with its own dependencies.
  const expandToPanelRef = useRef<() => void>(() => {});

  const {
    voice,
    voiceNotice,
    setVoiceNotice,
    showSendConfirmation,
    voiceTurnCompletionId,
    isVoiceTurnActive,
    startVoiceHold,
    confirmVoiceHold,
    resolveVoiceMode,
    endVoiceHold,
    cancelOpenMic,
    recoverCanceledOpenMic,
    expireCanceledOpenMic,
    showOpenMicEndingSoon,
    clearOpenMicEndingSoon,
  } = useFloatingAgentVoiceHold({
    store,
    floatingAgentPage,
    composerRegistryRef,
    foregroundMentionsRef,
    onExpandRequest: () => expandToPanelRef.current(),
  });

  const { hasUnread, peekQueue, markRepliesSeen, dismissPeekBubble, dismissAllPeeks } =
    useFloatingAgentReplies({
      store,
      floatingAgentPage,
      isPanelOpen: mode === "panel",
      isFocused: store.tabLifecycleManager.isTabActive,
    });

  // Provision the assistant, ensure its Native Chat session, and start the
  // live view. The iMessage claim flow owns its initial channel connection.
  useEffect(() => {
    void floatingAgentPage.initialize({
      ensureOnboardingSession: !readIMessageClaimFlowSession(),
    });
  }, [floatingAgentPage]);

  const acquireInteractionLease = useCallback(
    (panelOpenTarget?: FloatingAgentPanelOpenTarget): Promise<number | null> => {
      const pendingAcquire = interactionLeaseAcquirePromiseRef.current;
      if (pendingAcquire) return pendingAcquire;

      const acquireGeneration = interactionLeaseAcquireGenerationRef.current;
      const acquirePromise = (async () => {
        const result = await electronModule.floatingAgent.setInteractionActive({
          active: true,
          ...(panelOpenTarget ? { panelOpenTarget } : {}),
        });
        const acquiredLeaseId = readInteractionLeaseId(result?.interactionLeaseId);
        if (acquiredLeaseId === null) return null;

        if (interactionLeaseAcquireGenerationRef.current !== acquireGeneration) {
          await electronModule.floatingAgent.setInteractionActive({
            active: false,
            interactionLeaseId: acquiredLeaseId,
          });
          return null;
        }

        interactionLeaseIdRef.current = acquiredLeaseId;
        return acquiredLeaseId;
      })();

      interactionLeaseAcquirePromiseRef.current = acquirePromise;
      const clearPendingAcquire = () => {
        if (interactionLeaseAcquirePromiseRef.current === acquirePromise) {
          interactionLeaseAcquirePromiseRef.current = null;
        }
      };
      void acquirePromise.then(clearPendingAcquire, clearPendingAcquire);
      return acquirePromise;
    },
    []
  );

  const releaseInteractionLease = useCallback(async (interactionLeaseId: number): Promise<void> => {
    await electronModule.floatingAgent.setInteractionActive({
      active: false,
      interactionLeaseId,
    });
    if (interactionLeaseIdRef.current === interactionLeaseId) {
      interactionLeaseIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (voiceTurnCompletionId === lastVoiceTurnCompletionIdRef.current) {
      return;
    }
    lastVoiceTurnCompletionIdRef.current = voiceTurnCompletionId;
    if (
      voiceTurnCompletionId === 0 ||
      isVoiceTurnActive ||
      !clientEnvModule.isDesktop() ||
      modeRef.current === "panel"
    ) {
      return;
    }

    const interactionLeaseId = interactionLeaseIdRef.current;
    if (interactionLeaseId !== null) {
      void releaseInteractionLease(interactionLeaseId);
    }
  }, [isVoiceTurnActive, releaseInteractionLease, voiceTurnCompletionId]);

  const expandToPanel = useCallback(
    async (target?: FloatingAgentPanelOpenTarget, transferredInteractionLeaseId?: number) => {
      const transferredLeaseId = adoptInteractionLease(transferredInteractionLeaseId);
      if (isPanelOpen()) return;
      if (clientEnvModule.isDesktop() && transferredLeaseId === null) {
        const acquiredLeaseId = await acquireInteractionLease(target);
        if (acquiredLeaseId === null) return;
      }
      if (isPanelOpen() || panelExpansionPendingRef.current) return;

      panelExpansionPendingRef.current = true;
      // Resolved before markRepliesSeen() below advances the watermark.
      try {
        setPanelOpenTarget(resolveFloatingAgentOpenTarget(floatingAgentPage, target));
        floatingAgentPage.completeFirstMessageOnboardingIfNeeded("panel-open");
        setMode("panel");
        modeRef.current = "panel";
        markRepliesSeen();
      } finally {
        panelExpansionPendingRef.current = false;
      }
    },
    [
      acquireInteractionLease,
      adoptInteractionLease,
      floatingAgentPage,
      isPanelOpen,
      markRepliesSeen,
    ]
  );
  expandToPanelRef.current = () => void expandToPanel();

  // In-app surfaces (e.g. auto organize) ask the widget to open via the store;
  // the desktop widget takes the same request over IPC instead.
  const panelOpenRequestId = store.floatingAgent.panelOpenRequestId;
  const lastPanelOpenRequestId = useRef(panelOpenRequestId);
  useEffect(() => {
    if (panelOpenRequestId === lastPanelOpenRequestId.current) return;
    lastPanelOpenRequestId.current = panelOpenRequestId;
    void expandToPanel();
  }, [panelOpenRequestId, expandToPanel]);

  const collapseToCircle = useCallback(async () => {
    const interactionLeaseId = interactionLeaseIdRef.current;
    invalidatePendingInteractionLeaseAcquire();
    setMode("circle");
    // Dismiss whatever was on screen so it doesn't immediately re-pop; the
    // unread dot stays until the panel is opened.
    dismissAllPeeks();
    if (clientEnvModule.isDesktop() && interactionLeaseId !== null) {
      await releaseInteractionLease(interactionLeaseId);
    }
  }, [dismissAllPeeks, invalidatePendingInteractionLeaseAcquire, releaseInteractionLease]);

  const dockedEventRequestId = store.floatingAgent.dockedEventRequestId;
  useEffect(() => {
    if (!clientEnvModule.isDesktop()) return;

    const events = store.floatingAgent.consumeDockedEvents();
    for (const event of events) {
      if (event.kind === "summon") {
        // The summon key toggles: an open panel collapses back to the circle.
        if (isPanelOpen()) {
          void collapseToCircle();
        } else {
          void expandToPanel(undefined, event.interactionLeaseId);
        }
      } else if (event.kind === "onboarding-nudge") {
        if (onboardingNudgeTimerRef.current) clearTimeout(onboardingNudgeTimerRef.current);
        setIsOnboardingNudging(true);
        onboardingNudgeTimerRef.current = setTimeout(() => {
          setIsOnboardingNudging(false);
          onboardingNudgeTimerRef.current = null;
        }, 5_000);
      } else if (event.kind === "voice-hold-start") {
        adoptInteractionLease(event.interactionLeaseId);
        startVoiceHold();
      } else if (event.kind === "voice-hold-confirmed") {
        if (
          typeof event.voiceSessionId === "number" &&
          Number.isSafeInteger(event.voiceSessionId) &&
          (event.voiceMode === undefined ||
            event.voiceMode === "ptt" ||
            event.voiceMode === "hands-free")
        ) {
          confirmVoiceHold({
            voiceSessionId: event.voiceSessionId,
            mode: event.voiceMode,
          });
        }
      } else if (event.kind === "voice-hold-mode-resolved") {
        if (
          typeof event.voiceSessionId === "number" &&
          Number.isSafeInteger(event.voiceSessionId) &&
          (event.voiceMode === "ptt" || event.voiceMode === "hands-free")
        ) {
          resolveVoiceMode({
            voiceSessionId: event.voiceSessionId,
            mode: event.voiceMode,
          });
        }
      } else if (event.kind === "voice-hold-end") {
        void endVoiceHold({ canceled: event.canceled === true });
      } else if (event.kind === "voice-open-mic-canceled") {
        const countdown = readVoiceCountdown(event);
        if (countdown) cancelOpenMic(countdown);
      } else if (event.kind === "voice-open-mic-recover") {
        void recoverCanceledOpenMic();
      } else if (event.kind === "voice-open-mic-cancel-expired") {
        expireCanceledOpenMic();
      } else if (event.kind === "voice-open-mic-ending-soon") {
        const countdown = readVoiceCountdown(event);
        if (countdown) showOpenMicEndingSoon(countdown);
      } else if (event.kind === "voice-open-mic-continued") {
        clearOpenMicEndingSoon();
      }
    }
  }, [
    dockedEventRequestId,
    store.floatingAgent,
    expandToPanel,
    collapseToCircle,
    isPanelOpen,
    adoptInteractionLease,
    startVoiceHold,
    confirmVoiceHold,
    resolveVoiceMode,
    endVoiceHold,
    cancelOpenMic,
    recoverCanceledOpenMic,
    expireCanceledOpenMic,
    showOpenMicEndingSoon,
    clearOpenMicEndingSoon,
  ]);

  // The stack of undismissed unseen replies decides the mode; sizing is
  // pure CSS — the container grows upward from the pinned corner.
  const peekQueueLength = peekQueue.length;
  useEffect(() => {
    if (modeRef.current === "panel") return;
    if (peekQueueLength > 0) {
      setMode("peek");
    } else if (modeRef.current === "peek") {
      setMode("circle");
    }
  }, [peekQueueLength]);

  // Phone-width viewports get the mobile treatment; wider ones snap the panel
  // inside the app, resizable from the standalone agent's default size.
  // The store's screen-size updater publishes from a root mount effect, which
  // runs AFTER this widget's children mount in a first-commit panel — a panel
  // reading the unloaded value renders "floating" for one commit and its
  // composer autofocuses before the fullscreen variant lands. Fall back to the
  // same media query so the first render already knows it's a phone.
  const screenSize = store.interface.screenSizeState.data;
  const isPhone =
    screenSize !== undefined
      ? screenSize === ScreenSize.Phone
      : window.matchMedia(MDSBreakpoints[ScreenSize.Phone]).matches;

  /* A hover-expanded bubble grows upward from the pinned corner, so the tab's
     own height is the ceiling — past it the message would be cut off by the
     viewport with no way to scroll to it. Tracked in state because a window
     resize (or a devtools open) changes the budget while a bubble is expanded. */
  const [viewportHeightPx, setViewportHeightPx] = useState(() => window.innerHeight);
  useEffect(() => {
    const handleResize = () => setViewportHeightPx(window.innerHeight);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxExpandedBubbleHeightPx = maxExpandedPeekBubbleHeightPx({
    // The widget root sits this far up from the bottom, so that strip is not
    // the stack's to grow into. Phones pin it flush and give up nothing.
    availableHeightPx:
      viewportHeightPx - (isPhone ? MOBILE_WEB_WIDGET_HOST_INSET_PX : WEB_WIDGET_HOST_INSET_PX),
    bubbleCount: peekQueueLength,
    hasVoiceSessionNotice: false,
  });

  const panelVariant = (() => {
    if (isPhone) return "fullscreen";
    if (clientEnvModule.isDesktop()) return "docked";
    return "floating";
  })();
  const holdToTalkKey = store.spaceAccountSettings.memAgent.holdToTalkKey;
  const resolvedHoldToTalkKey = holdToTalkKey ?? getDefaultMemAgentHoldToTalkKey();
  const hotkeyLabel = store.spaceAccountSettings.memAgent.holdToTalkIsEnabled
    ? getMemAgentHoldToTalkKeyDisplay(resolvedHoldToTalkKey).tooltipKey
    : null;
  const pushToTalkTooltipLabel = clientEnvModule.isDesktop()
    ? hotkeyLabel
      ? `Press and hold ${hotkeyLabel} to remember`
      : "Enable Push-to-Remember in settings"
    : "Download the desktop app for push to talk";

  return createPortal(
    <StyledWebWidgetRoot
      data-component="FloatingAgentWebWidget"
      // React-Aria's ariaHideOutside marks every sibling of an open MDSModal
      // (e.g. the onboarding wizard) `inert`, which swallows clicks on this
      // portal despite its higher z-index. This marker is the documented
      // opt-out — ariaHideOutside skips top-layer nodes and their subtree.
      data-react-aria-top-layer="true"
      $fullscreen={isPhone && mode === "panel"}
      $isPhone={isPhone}
    >
      {mode === "panel" ? (
        <StyledWebPanelSurface>
          <FloatingAgentPanel
            floatingAgentPage={floatingAgentPage}
            voice={voice}
            voiceNotice={voiceNotice}
            composerRegistryRef={composerRegistryRef}
            openTarget={panelOpenTarget}
            foregroundMentions={foregroundMentions}
            variant={panelVariant}
            onDismissVoiceNotice={() => setVoiceNotice(null)}
            onCollapse={collapseToCircle}
            onCancelVoice={() => void endVoiceHold({ canceled: true })}
            onConfirmVoice={() => void endVoiceHold()}
          />
        </StyledWebPanelSurface>
      ) : (
        <StyledWebStack $isPhone={isPhone}>
          {/* Bottom-right corner: bubbles stack upward, newest adjacent to
              the circle (tail points at it) — the desktop bottom-anchor
              layout. */}
          {mode === "peek" &&
            peekQueue.map((reply, index, stack) => (
              <FloatingAgentPreviewBubble
                key={reply.eventId}
                content={reply.text}
                expansionKey={reply.eventId}
                anchor={FloatingAgentWindowAnchor.BottomRight}
                showTail={index === stack.length - 1}
                maxExpandedHeightPx={maxExpandedBubbleHeightPx}
                onOpen={() =>
                  void expandToPanel(
                    {
                      eventId: reply.eventId,
                      threadRootId:
                        floatingAgentPage.threadAnchorByEventId.get(reply.eventId) ?? null,
                    },
                    undefined
                  )
                }
                onDismiss={() => dismissPeekBubble(reply.eventId)}
              />
            ))}
          <MDSTooltip
            config={{
              label: pushToTalkTooltipLabel,
              placement: MDSTooltipPlacement.Top,
            }}
          >
            <MDSTooltipTrigger>
              <FloatingAgentCircle
                voice={voice}
                showSendConfirmation={showSendConfirmation}
                isResizingShrink={false}
                hasUnread={hasUnread}
                isThinking={isThinking}
                isOnboardingNudging={isOnboardingNudging}
                onExpand={() => void expandToPanel()}
                onHoverChange={() => {}}
                onDragStart={() => {}}
              />
            </MDSTooltipTrigger>
          </MDSTooltip>
        </StyledWebStack>
      )}
    </StyledWebWidgetRoot>,
    document.body
  );
});

/* Above page content and the sidebar, below MDS modals/popovers (100k+) and
   toasts. The bottom-right corner is uncontested — toasts are bottom-center.
   The safe-area inset keeps the circle clear of the iOS home indicator. */
const StyledWebWidgetRoot = styled.div<{ $fullscreen: boolean; $isPhone: boolean }>`
  position: fixed;
  z-index: ${MDSZIndexes.FloatingActions};
  pointer-events: none;
  --floating-agent-enter-origin: bottom right;
  ${({ $fullscreen, $isPhone }) =>
    $fullscreen
      ? `inset: 0;`
      : `
        bottom: calc(${
          $isPhone ? MOBILE_WEB_WIDGET_HOST_INSET_PX : WEB_WIDGET_HOST_INSET_PX
        }px + env(safe-area-inset-bottom));
        right: ${$isPhone ? MOBILE_WEB_WIDGET_HOST_INSET_PX : WEB_WIDGET_HOST_INSET_PX}px;
      `}
`;

const StyledWebPanelSurface = styled.div`
  pointer-events: auto;
`;

const StyledWebStack = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ $isPhone }) => ($isPhone ? MOBILE_WEB_WIDGET_STACK_GAP_PX : WEB_WIDGET_STACK_GAP_PX)}px;
`;
